import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Check, CheckCheck, FileText, DollarSign, AlertTriangle, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const ICON_MAP = {
  report_submitted: FileText,
  payment_recorded: DollarSign,
  invoice_overdue: AlertTriangle,
};

const COLOR_MAP = {
  report_submitted: "text-emerald-400",
  payment_recorded: "text-blue-400",
  invoice_overdue: "text-orange-400",
};

function NotificationItem({ notification, onMarkRead }) {
  const Icon = ICON_MAP[notification.type] || Bell;
  const colorClass = COLOR_MAP[notification.type] || "text-muted-foreground";
  const timeAgo = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 border-b border-border transition-colors ${notification.read ? "opacity-60" : "bg-primary/5"}`}
      data-testid={`notification-item-${notification.id}`}
    >
      <div className={`mt-0.5 ${colorClass}`}>
        <Icon className="h-4 w-4" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight">{notification.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{notification.message}</p>
        <p className="text-[10px] text-muted-foreground/60 mt-1 font-mono">{timeAgo}</p>
      </div>
      {!notification.read && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0"
          onClick={(e) => { e.stopPropagation(); onMarkRead(notification.id); }}
          data-testid={`mark-read-${notification.id}`}
        >
          <Check className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

export function NotificationBell() {
  const { user } = useAuth();
  const { navigate } = useTenantNavigation();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const sseRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await api.get("/notifications/unread-count");
      setUnreadCount(data.unread_count);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/notifications?limit=20");
      setNotifications(data.notifications);
    } catch {}
    setLoading(false);
  }, []);

  // Fetch unread count on mount + poll every 30s
  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  // SSE for real-time updates
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    const url = `${API_BASE}/api/notifications/stream?authorization=Bearer ${token}`;

    let es;
    try {
      es = new EventSource(url);
      sseRef.current = es;

      es.onmessage = (event) => {
        try {
          const notif = JSON.parse(event.data);
          setNotifications((prev) => [notif, ...prev].slice(0, 20));
          setUnreadCount((c) => c + 1);
        } catch {}
      };

      es.onerror = () => {
        es.close();
        // Reconnect after 10s
        setTimeout(() => {
          if (sseRef.current === es) {
            sseRef.current = null;
          }
        }, 10000);
      };
    } catch {}

    return () => {
      if (es) es.close();
    };
  }, [user]);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Load notifications when panel opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const handleMarkRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const togglePanel = () => setOpen((v) => !v);

  if (!user || user.role === "contractor") return null;

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={togglePanel}
        className="relative"
        data-testid="notification-bell"
      >
        <Bell className="h-4 w-4" strokeWidth={1.5} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-lg border border-border bg-card shadow-xl z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
            <h3 className="font-headline text-sm font-semibold">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-muted-foreground"
                  onClick={handleMarkAllRead}
                  data-testid="mark-all-read"
                >
                  <CheckCheck className="h-3.5 w-3.5 mr-1" /> Mark all read
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationItem key={n.id} notification={n} onMarkRead={handleMarkRead} />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-border bg-card">
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-xs text-muted-foreground"
                onClick={() => { navigate("/notifications"); setOpen(false); }}
                data-testid="view-all-notifications"
              >
                View all notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
